import { useState, useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface DualFileUploadProps {
  onFilesUploaded: (rfp: File, proposal: File) => void;
}

interface FileDropzoneProps {
  label: string;
  file: File | null;
  onFileSelected: (file: File) => void;
  onFileRejected: (fileName: string) => void;
  icon: React.ReactNode;
}

const FileDropzone = ({ label, file, onFileSelected, onFileRejected, icon }: FileDropzoneProps) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onFileSelected(acceptedFiles[0]);
    }
  }, [onFileSelected]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      onFileRejected(fileRejections[0].file.name);
    }
  }, [onFileRejected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    maxFiles: 1
  });

  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box textAlign="center" mb={2}>
          {icon}
          <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
            {label}
          </Typography>
        </Box>

        <Paper
          {...getRootProps()}
          elevation={0}
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: file ? 'success.main' : isDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backgroundColor: file
              ? 'rgba(46, 125, 50, 0.08)' // More transparent green overlay
              : isDragActive ? 'action.hover' : 'background.paper',
            '&:hover': {
              borderColor: file ? 'success.main' : 'primary.main',
              backgroundColor: file
                ? 'rgba(46, 125, 50, 0.12)' // Slightly more opaque on hover
                : 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />

          {file ? (
            <Box>
              <CheckCircleIcon
                sx={{
                  fontSize: 48,
                  color: 'success.main',
                  mb: 1,
                }}
              />
              <Typography variant="body1" fontWeight={600} color="success.dark" gutterBottom>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Click to change file
              </Typography>
            </Box>
          ) : isDragActive ? (
            <Box>
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 1,
                }}
              />
              <Typography variant="body1" color="primary" fontWeight={600}>
                Drop file here
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: 'text.secondary',
                  mb: 1,
                }}
              />
              <Typography variant="body1" gutterBottom>
                Drag and drop file
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                .txt, .md, .pdf, .docx, .doc
              </Typography>
            </Box>
          )}
        </Paper>
      </CardContent>
    </Card>
  );
};

export default function DualFileUpload({ onFilesUploaded }: DualFileUploadProps) {
  const [rfpFile, setRfpFile] = useState<File | null>(null);
  const [proposalFile, setProposalFile] = useState<File | null>(null);
  const [invalidFileDialogOpen, setInvalidFileDialogOpen] = useState(false);
  const [rejectedFileName, setRejectedFileName] = useState<string>('');

  const handleFileRejected = useCallback((fileName: string) => {
    setRejectedFileName(fileName);
    setInvalidFileDialogOpen(true);
  }, []);

  const handleSubmit = () => {
    if (rfpFile && proposalFile) {
      onFilesUploaded(rfpFile, proposalFile);
    }
  };

  const canSubmit = rfpFile !== null && proposalFile !== null;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          Proposal Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your RFP and proposal documents to receive AI-powered evaluation
        </Typography>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FileDropzone
            label="RFP Document"
            file={rfpFile}
            onFileSelected={setRfpFile}
            onFileRejected={handleFileRejected}
            icon={<AssignmentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FileDropzone
            label="Proposal Document"
            file={proposalFile}
            onFileSelected={setProposalFile}
            onFileRejected={handleFileRejected}
            icon={<DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />}
          />
        </Grid>
      </Grid>

      <Box textAlign="center">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {canSubmit && (
            <Chip
              label="Both files uploaded - ready to review"
              color="success"
              variant="outlined"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            />
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              maxWidth: 400,
              width: '100%',
              py: 1.5,
              fontSize: '1.1rem',
            }}
          >
            Start Review
          </Button>
          {!canSubmit && (
            <Typography variant="caption" color="text.secondary">
              Please upload both RFP and proposal documents
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog
        open={invalidFileDialogOpen}
        onClose={() => setInvalidFileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="error" />
          Invalid File Format
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The file <strong>{rejectedFileName}</strong> is not a supported format.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please upload a file in one of the following formats:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0 }}>
            <li><Typography variant="body2">.txt (Plain Text)</Typography></li>
            <li><Typography variant="body2">.md (Markdown)</Typography></li>
            <li><Typography variant="body2">.pdf (PDF Document)</Typography></li>
            <li><Typography variant="body2">.docx (Microsoft Word 2007+)</Typography></li>
            <li><Typography variant="body2">.doc (Microsoft Word 97-2003)</Typography></li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInvalidFileDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
